import React, { useRef, useState, useEffect, useCallback } from 'react';
import { RotateCcw, Undo2, Grid3x3, Plus, X, Camera, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const DOT_SPACING = 36;
const DOT_RADIUS = 2.5;
const SNAP_RADIUS = 22;

const ROOM_TYPES = [
    'Lounge', 'Dining Room', 'Kitchen', 'Study',
    'Bedroom', 'Bathroom', 'Cloakroom', 'Hallway', 'Landing',
];

const WALL_WORK_OPTIONS = [
    'Blue Grit & Overskim',
    'Board & Overskim',
    'Dot & Dab & Skim',
];

const CEILING_WORK_OPTIONS = [
    'Blue Grit & Overskim',
    'Board & Overskim',
];

// --- Reusable sub-components ---

function SectionHeading({ children }) {
    return (
        <h3 className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
            {children}
        </h3>
    );
}

function FieldLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            {children}
        </label>
    );
}

function YesNoToggle({ value, onChange, testId }) {
    return (
        <div className="flex gap-1.5" data-testid={testId}>
            <button
                type="button"
                onClick={() => onChange(true)}
                className={`h-9 px-4 rounded-lg text-xs font-semibold transition-colors ${value === true ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                data-testid={testId ? `${testId}-yes` : undefined}
            >
                Yes
            </button>
            <button
                type="button"
                onClick={() => onChange(false)}
                className={`h-9 px-4 rounded-lg text-xs font-semibold transition-colors ${value === false ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                data-testid={testId ? `${testId}-no` : undefined}
            >
                No
            </button>
        </div>
    );
}

function SelectDropdown({ value, onChange, options, placeholder, testId }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full h-11 px-3 text-sm font-medium text-slate-900 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors appearance-none"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
        >
            <option value="">{placeholder || 'Select...'}</option>
            {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
            ))}
        </select>
    );
}

function QuestionRow({ label, children }) {
    return (
        <div className="py-3 first:pt-0 last:pb-0">
            <p className="text-sm font-medium text-slate-700 mb-2">{label}</p>
            {children}
        </div>
    );
}

// --- Main Component ---

export default function RoomDrawing() {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const photoInputRef = useRef(null);

    // Canvas state
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const [selectedDots, setSelectedDots] = useState([]);
    const [isClosed, setIsClosed] = useState(false);
    const [hoverDot, setHoverDot] = useState(null);

    // Room & measurement state
    const [roomType, setRoomType] = useState('');
    const [wallMeasurements, setWallMeasurements] = useState({});
    const [roomHeight, setRoomHeight] = useState('');

    // Plastering spec state
    const [allWallsSame, setAllWallsSame] = useState(null);
    const [singleWallWork, setSingleWallWork] = useState('');
    const [perWallWork, setPerWallWork] = useState({});
    const [ceilingPlastered, setCeilingPlastered] = useState(null);
    const [ceilingWorkType, setCeilingWorkType] = useState('');
    const [ceilingInsulated, setCeilingInsulated] = useState(null);
    const [wallsInsulated, setWallsInsulated] = useState(null);
    const [insulatedWalls, setInsulatedWalls] = useState([]);
    const [wallPrepRequired, setWallPrepRequired] = useState(null);
    const [prepHours, setPrepHours] = useState('');
    const [plasterBeads, setPlasterBeads] = useState('');

    // Additional items
    const [additionalItems, setAdditionalItems] = useState([]);

    // Photos & notes
    const [photos, setPhotos] = useState([]);
    const [notes, setNotes] = useState('');

    // ----- Canvas logic (unchanged) -----

    const getDots = useCallback(() => {
        if (canvasSize.width === 0) return [];
        const dots = [];
        const cols = Math.floor((canvasSize.width - DOT_SPACING) / DOT_SPACING);
        const rows = Math.floor((canvasSize.height - DOT_SPACING) / DOT_SPACING);
        const offsetX = (canvasSize.width - cols * DOT_SPACING) / 2;
        const offsetY = (canvasSize.height - rows * DOT_SPACING) / 2;
        for (let r = 0; r <= rows; r++) {
            for (let c = 0; c <= cols; c++) {
                dots.push({ x: offsetX + c * DOT_SPACING, y: offsetY + r * DOT_SPACING });
            }
        }
        return dots;
    }, [canvasSize]);

    const findNearestDot = useCallback((x, y) => {
        const dots = getDots();
        let nearest = null;
        let minDist = Infinity;
        for (const dot of dots) {
            const dist = Math.hypot(dot.x - x, dot.y - y);
            if (dist < minDist && dist < SNAP_RADIUS) {
                minDist = dist;
                nearest = dot;
            }
        }
        return nearest;
    }, [getDots]);

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const w = containerRef.current.clientWidth;
                const h = Math.min(Math.max(w * 0.85, 300), 480);
                setCanvasSize({ width: w, height: h });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || canvasSize.width === 0) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        canvas.width = canvasSize.width * dpr;
        canvas.height = canvasSize.height * dpr;
        canvas.style.width = `${canvasSize.width}px`;
        canvas.style.height = `${canvasSize.height}px`;
        ctx.scale(dpr, dpr);

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

        const dots = getDots();
        for (const dot of dots) {
            ctx.beginPath();
            ctx.arc(dot.x, dot.y, DOT_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = '#CBD5E1';
            ctx.fill();
        }

        if (hoverDot && !isClosed) {
            ctx.beginPath();
            ctx.arc(hoverDot.x, hoverDot.y, 13, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(220,38,38,0.10)';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(hoverDot.x, hoverDot.y, DOT_RADIUS + 2, 0, Math.PI * 2);
            ctx.fillStyle = '#DC2626';
            ctx.fill();
        }

        if (isClosed && selectedDots.length >= 3) {
            ctx.beginPath();
            ctx.moveTo(selectedDots[0].x, selectedDots[0].y);
            for (let i = 1; i < selectedDots.length; i++) {
                ctx.lineTo(selectedDots[i].x, selectedDots[i].y);
            }
            ctx.closePath();
            ctx.fillStyle = 'rgba(220,38,38,0.06)';
            ctx.fill();
        }

        const drawWallLabel = (from, to, wallNum) => {
            const midX = (from.x + to.x) / 2;
            const midY = (from.y + to.y) / 2;
            const dx = to.x - from.x;
            const dy = to.y - from.y;
            const len = Math.hypot(dx, dy);
            if (len === 0) return;
            let nx = -dy / len;
            let ny = dx / len;
            if (isClosed && selectedDots.length >= 3) {
                const cx = selectedDots.reduce((s, d) => s + d.x, 0) / selectedDots.length;
                const cy = selectedDots.reduce((s, d) => s + d.y, 0) / selectedDots.length;
                const testX = midX + nx * 10;
                const testY = midY + ny * 10;
                if (Math.hypot(testX - cx, testY - cy) < Math.hypot(midX - cx, midY - cy)) {
                    nx = -nx;
                    ny = -ny;
                }
            }
            const offset = 16;
            const labelX = midX + nx * offset;
            const labelY = midY + ny * offset;
            const text = `W${wallNum}`;
            ctx.font = '600 10px "Public Sans", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const metrics = ctx.measureText(text);
            const pw = metrics.width + 10;
            const ph = 17;
            const r = 4;
            const x0 = labelX - pw / 2;
            const y0 = labelY - ph / 2;
            ctx.beginPath();
            ctx.moveTo(x0 + r, y0);
            ctx.lineTo(x0 + pw - r, y0);
            ctx.quadraticCurveTo(x0 + pw, y0, x0 + pw, y0 + r);
            ctx.lineTo(x0 + pw, y0 + ph - r);
            ctx.quadraticCurveTo(x0 + pw, y0 + ph, x0 + pw - r, y0 + ph);
            ctx.lineTo(x0 + r, y0 + ph);
            ctx.quadraticCurveTo(x0, y0 + ph, x0, y0 + ph - r);
            ctx.lineTo(x0, y0 + r);
            ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
            ctx.closePath();
            ctx.fillStyle = '#1E293B';
            ctx.fill();
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText(text, labelX, labelY + 0.5);
        };

        const wallSegments = [];
        for (let i = 0; i < selectedDots.length - 1; i++) {
            wallSegments.push([selectedDots[i], selectedDots[i + 1]]);
        }
        if (isClosed && selectedDots.length >= 3) {
            wallSegments.push([selectedDots[selectedDots.length - 1], selectedDots[0]]);
        }
        for (let i = 0; i < wallSegments.length; i++) {
            const [from, to] = wallSegments[i];
            ctx.beginPath();
            ctx.moveTo(from.x, from.y);
            ctx.lineTo(to.x, to.y);
            ctx.strokeStyle = '#1E293B';
            ctx.lineWidth = 2.5;
            ctx.lineCap = 'round';
            ctx.stroke();
            drawWallLabel(from, to, i + 1);
        }

        if (!isClosed && selectedDots.length > 0 && hoverDot) {
            const last = selectedDots[selectedDots.length - 1];
            if (last.x !== hoverDot.x || last.y !== hoverDot.y) {
                ctx.beginPath();
                ctx.moveTo(last.x, last.y);
                ctx.lineTo(hoverDot.x, hoverDot.y);
                ctx.strokeStyle = 'rgba(220,38,38,0.35)';
                ctx.lineWidth = 1.5;
                ctx.setLineDash([6, 4]);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }

        for (let i = 0; i < selectedDots.length; i++) {
            const dot = selectedDots[i];
            if (i === 0 && !isClosed && selectedDots.length >= 2) {
                ctx.beginPath();
                ctx.arc(dot.x, dot.y, 14, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(220,38,38,0.30)';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
            ctx.beginPath();
            ctx.arc(dot.x, dot.y, 5.5, 0, Math.PI * 2);
            ctx.fillStyle = i === 0 ? '#DC2626' : '#1E293B';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(dot.x, dot.y, 2, 0, Math.PI * 2);
            ctx.fillStyle = '#FFFFFF';
            ctx.fill();
        }
    }, [canvasSize, selectedDots, isClosed, hoverDot, getDots]);

    const handleCanvasInteraction = useCallback((clientX, clientY) => {
        if (isClosed) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const nearest = findNearestDot(x, y);
        if (!nearest) return;

        if (selectedDots.length >= 3) {
            const first = selectedDots[0];
            if (Math.hypot(nearest.x - first.x, nearest.y - first.y) < 1) {
                setIsClosed(true);
                const measurements = {};
                for (let i = 0; i < selectedDots.length; i++) {
                    measurements[i] = '';
                }
                setWallMeasurements(measurements);
                return;
            }
        }

        if (selectedDots.length > 0) {
            const last = selectedDots[selectedDots.length - 1];
            if (nearest.x === last.x && nearest.y === last.y) return;
        }

        for (let i = 1; i < selectedDots.length; i++) {
            if (nearest.x === selectedDots[i].x && nearest.y === selectedDots[i].y) return;
        }

        setSelectedDots(prev => [...prev, nearest]);
    }, [isClosed, selectedDots, findNearestDot]);

    const handleClick = (e) => handleCanvasInteraction(e.clientX, e.clientY);
    const handleTouchEnd = (e) => {
        e.preventDefault();
        if (e.changedTouches.length > 0) {
            const t = e.changedTouches[0];
            handleCanvasInteraction(t.clientX, t.clientY);
        }
    };
    const handleMouseMove = (e) => {
        if (isClosed) { setHoverDot(null); return; }
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        setHoverDot(findNearestDot(e.clientX - rect.left, e.clientY - rect.top));
    };
    const handleMouseLeave = () => setHoverDot(null);

    const handleReset = () => {
        setSelectedDots([]);
        setIsClosed(false);
        setWallMeasurements({});
        setRoomHeight('');
        setHoverDot(null);
        setRoomType('');
        setAllWallsSame(null);
        setSingleWallWork('');
        setPerWallWork({});
        setCeilingPlastered(null);
        setCeilingWorkType('');
        setCeilingInsulated(null);
        setWallsInsulated(null);
        setInsulatedWalls([]);
        setWallPrepRequired(null);
        setPrepHours('');
        setPlasterBeads('');
        setAdditionalItems([]);
        setPhotos([]);
        setNotes('');
    };

    const handleUndo = () => {
        if (isClosed || selectedDots.length === 0) return;
        setSelectedDots(prev => prev.slice(0, -1));
    };

    // ----- Additional Items handlers -----

    const addAdditionalItem = () => {
        setAdditionalItems(prev => [...prev, { id: Date.now(), description: '', hours: '', value: '' }]);
    };

    const updateAdditionalItem = (id, field, val) => {
        setAdditionalItems(prev => prev.map(item => item.id === id ? { ...item, [field]: val } : item));
    };

    const removeAdditionalItem = (id) => {
        setAdditionalItems(prev => prev.filter(item => item.id !== id));
    };

    // ----- Photo handlers -----

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files || []);
        const newPhotos = files.map(file => ({
            id: Date.now() + Math.random(),
            file,
            preview: URL.createObjectURL(file),
        }));
        setPhotos(prev => [...prev, ...newPhotos]);
        if (photoInputRef.current) photoInputRef.current.value = '';
    };

    const removePhoto = (id) => {
        setPhotos(prev => {
            const photo = prev.find(p => p.id === id);
            if (photo) URL.revokeObjectURL(photo.preview);
            return prev.filter(p => p.id !== id);
        });
    };

    // ----- Insulated walls toggle -----

    const toggleInsulatedWall = (wallIdx) => {
        setInsulatedWalls(prev =>
            prev.includes(wallIdx) ? prev.filter(w => w !== wallIdx) : [...prev, wallIdx]
        );
    };

    // ----- Calculations -----

    const wallCount = isClosed ? selectedDots.length : 0;
    const allMeasurementsFilled = wallCount > 0 &&
        Object.keys(wallMeasurements).length === wallCount &&
        Object.values(wallMeasurements).every(v => v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0) &&
        roomHeight !== '' && !isNaN(parseFloat(roomHeight)) && parseFloat(roomHeight) > 0;

    const wallAreas = allMeasurementsFilled
        ? Array.from({ length: wallCount }, (_, i) => parseFloat(wallMeasurements[i]) * parseFloat(roomHeight))
        : [];
    const totalWallArea = wallAreas.reduce((s, a) => s + a, 0);

    const statusText = isClosed
        ? `${wallCount} walls drawn`
        : selectedDots.length === 0
            ? 'Tap a dot to start drawing'
            : selectedDots.length === 1
                ? 'Tap another dot to draw Wall 1'
                : `${selectedDots.length - 1} wall${selectedDots.length - 1 > 1 ? 's' : ''} drawn — tap the red dot to close`;

    const handleSaveRoom = () => {
        toast.success('Room saved successfully');
    };

    // Shared input class
    const inputCls = "w-full h-11 px-3 text-sm font-medium text-slate-900 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors placeholder:text-slate-300";

    return (
        <div className="min-h-screen bg-white pb-6" data-testid="room-drawing-page">
            {/* Header */}
            <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-200">
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                            <Grid3x3 className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h1 className="font-heading text-lg font-bold text-slate-900 leading-tight tracking-tight">
                                Room Drawing
                            </h1>
                            <p className="text-[11px] text-slate-500 leading-tight mt-0.5" data-testid="status-text">
                                {statusText}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-1.5">
                        {!isClosed && selectedDots.length > 0 && (
                            <button
                                onClick={handleUndo}
                                data-testid="undo-btn"
                                className="h-8 px-2.5 flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                <Undo2 className="w-3.5 h-3.5" />
                                Undo
                            </button>
                        )}
                        <button
                            onClick={handleReset}
                            data-testid="reset-btn"
                            className="h-8 px-2.5 flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Reset
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-lg mx-auto">
                {/* Room Type Selector */}
                <div className="px-4 pt-4 pb-1">
                    <FieldLabel htmlFor="room-type">Room Type</FieldLabel>
                    <SelectDropdown
                        value={roomType}
                        onChange={setRoomType}
                        options={ROOM_TYPES}
                        placeholder="Select room type..."
                        testId="room-type-select"
                    />
                </div>

                {/* Canvas */}
                <div ref={containerRef} className="px-3 pt-3">
                    <div
                        className="border border-slate-200 rounded-xl overflow-hidden bg-white"
                        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.02)' }}
                    >
                        <canvas
                            ref={canvasRef}
                            data-testid="room-canvas"
                            onClick={handleClick}
                            onTouchEnd={handleTouchEnd}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                            className="block w-full touch-none"
                            style={{ cursor: isClosed ? 'default' : 'crosshair' }}
                        />
                    </div>
                    {!isClosed && selectedDots.length >= 2 && (
                        <p className="text-center text-[11px] text-red-600/70 mt-2 font-medium">
                            Tap the red starting dot to close the room shape
                        </p>
                    )}
                </div>

                {/* Everything below the canvas: shown after shape is closed */}
                {isClosed && (
                    <div className="px-4 pt-5" data-testid="measurements-panel">

                        {/* Room Height */}
                        <div className="mb-5">
                            <FieldLabel>Room Height</FieldLabel>
                            <div className="relative">
                                <input
                                    type="number" inputMode="decimal" step="0.01" min="0"
                                    value={roomHeight}
                                    onChange={(e) => setRoomHeight(e.target.value)}
                                    placeholder="e.g. 2.4"
                                    data-testid="room-height-input"
                                    className={`${inputCls} pr-10 h-12 text-base`}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">m</span>
                            </div>
                        </div>

                        {/* Wall Measurements */}
                        <div className="mb-5">
                            <FieldLabel>Wall Measurements</FieldLabel>
                            <div className="grid grid-cols-2 gap-2.5">
                                {Array.from({ length: wallCount }, (_, i) => (
                                    <div key={i}>
                                        <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                                            Wall {i + 1}
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="number" inputMode="decimal" step="0.01" min="0"
                                                value={wallMeasurements[i] || ''}
                                                onChange={(e) => setWallMeasurements(prev => ({ ...prev, [i]: e.target.value }))}
                                                placeholder="0.00"
                                                data-testid={`wall-${i + 1}-input`}
                                                className={`${inputCls} pr-10`}
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-medium">m</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Summary */}
                        {allMeasurementsFilled && (
                            <div data-testid="summary-panel" className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                                <SectionHeading>Summary</SectionHeading>
                                <div className="space-y-1.5">
                                    {Array.from({ length: wallCount }, (_, i) => {
                                        const len = parseFloat(wallMeasurements[i]);
                                        const h = parseFloat(roomHeight);
                                        const area = len * h;
                                        return (
                                            <div key={i} className="flex justify-between items-center text-sm">
                                                <span className="text-slate-600">
                                                    <span className="font-semibold text-slate-800">Wall {i + 1}</span>
                                                    {' '}{len.toFixed(2)}m &times; {h.toFixed(2)}m
                                                </span>
                                                <span className="font-medium text-slate-700 tabular-nums">{area.toFixed(2)} m&sup2;</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-3 pt-3 border-t border-slate-300 flex justify-between items-center">
                                    <span className="text-sm font-bold text-slate-900">Total Wall Area</span>
                                    <span className="text-lg font-bold text-red-600 tabular-nums" data-testid="total-area">
                                        {totalWallArea.toFixed(2)} m&sup2;
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* ==================== PLASTERING SPECIFICATION ==================== */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="plastering-spec">
                            <SectionHeading>Plastering Specification</SectionHeading>

                            {/* 1. Wall Work Type */}
                            <QuestionRow label="Are all walls being plastered the same way?">
                                <YesNoToggle value={allWallsSame} onChange={setAllWallsSame} testId="all-walls-same" />
                                {allWallsSame === true && (
                                    <div className="mt-3">
                                        <SelectDropdown
                                            value={singleWallWork}
                                            onChange={setSingleWallWork}
                                            options={WALL_WORK_OPTIONS}
                                            placeholder="Select work type..."
                                            testId="single-wall-work-select"
                                        />
                                    </div>
                                )}
                                {allWallsSame === false && (
                                    <div className="mt-3 space-y-2">
                                        {Array.from({ length: wallCount }, (_, i) => (
                                            <div key={i}>
                                                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Wall {i + 1}</label>
                                                <SelectDropdown
                                                    value={perWallWork[i] || ''}
                                                    onChange={(val) => setPerWallWork(prev => ({ ...prev, [i]: val }))}
                                                    options={WALL_WORK_OPTIONS}
                                                    placeholder="Select..."
                                                    testId={`wall-${i + 1}-work-select`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </QuestionRow>

                            {/* 2. Ceiling */}
                            <QuestionRow label="Is the ceiling being plastered?">
                                <YesNoToggle value={ceilingPlastered} onChange={setCeilingPlastered} testId="ceiling-plastered" />
                                {ceilingPlastered === true && (
                                    <div className="mt-3 space-y-3">
                                        <SelectDropdown
                                            value={ceilingWorkType}
                                            onChange={setCeilingWorkType}
                                            options={CEILING_WORK_OPTIONS}
                                            placeholder="Select ceiling work type..."
                                            testId="ceiling-work-select"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-slate-700 mb-2">Is the ceiling being insulated?</p>
                                            <YesNoToggle value={ceilingInsulated} onChange={setCeilingInsulated} testId="ceiling-insulated" />
                                        </div>
                                    </div>
                                )}
                            </QuestionRow>

                            {/* 3. Wall Insulation */}
                            <QuestionRow label="Are any walls being insulated?">
                                <YesNoToggle value={wallsInsulated} onChange={(val) => { setWallsInsulated(val); if (!val) setInsulatedWalls([]); }} testId="walls-insulated" />
                                {wallsInsulated === true && (
                                    <div className="mt-3 flex flex-wrap gap-2" data-testid="insulated-walls-checklist">
                                        {Array.from({ length: wallCount }, (_, i) => {
                                            const checked = insulatedWalls.includes(i);
                                            return (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    onClick={() => toggleInsulatedWall(i)}
                                                    data-testid={`insulated-wall-${i + 1}`}
                                                    className={`h-9 px-3.5 rounded-lg text-xs font-semibold border transition-colors ${checked
                                                        ? 'bg-slate-900 text-white border-slate-900'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                                        }`}
                                                >
                                                    Wall {i + 1}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </QuestionRow>

                            {/* 4. Wall Preparation */}
                            <QuestionRow label="Is any wall preparation required?">
                                <YesNoToggle value={wallPrepRequired} onChange={setWallPrepRequired} testId="wall-prep" />
                                {wallPrepRequired === true && (
                                    <div className="mt-3">
                                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Preparation hours</label>
                                        <input
                                            type="number" inputMode="decimal" step="0.5" min="0"
                                            value={prepHours}
                                            onChange={(e) => setPrepHours(e.target.value)}
                                            placeholder="0"
                                            data-testid="prep-hours-input"
                                            className={inputCls}
                                        />
                                    </div>
                                )}
                            </QuestionRow>

                            {/* 5. Plaster Beads */}
                            <QuestionRow label="Number of plaster beads required">
                                <input
                                    type="number" inputMode="numeric" step="1" min="0"
                                    value={plasterBeads}
                                    onChange={(e) => setPlasterBeads(e.target.value)}
                                    placeholder="0"
                                    data-testid="plaster-beads-input"
                                    className={inputCls}
                                />
                            </QuestionRow>
                        </div>

                        {/* ==================== ADDITIONAL ITEMS ==================== */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="additional-items-section">
                            <div className="flex items-center justify-between mb-3">
                                <SectionHeading>Additional Items</SectionHeading>
                                <button
                                    type="button"
                                    onClick={addAdditionalItem}
                                    data-testid="add-item-btn"
                                    className="h-8 px-3 flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add Item
                                </button>
                            </div>
                            {additionalItems.length === 0 && (
                                <p className="text-sm text-slate-400 italic">No additional items added.</p>
                            )}
                            <div className="space-y-3">
                                {additionalItems.map((item, idx) => (
                                    <div key={item.id} className="bg-slate-50 border border-slate-200 rounded-xl p-3 relative" data-testid={`additional-item-${idx}`}>
                                        <button
                                            type="button"
                                            onClick={() => removeAdditionalItem(item.id)}
                                            data-testid={`remove-item-${idx}`}
                                            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                        <div className="space-y-2 pr-6">
                                            <div>
                                                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Description</label>
                                                <input
                                                    type="text"
                                                    value={item.description}
                                                    onChange={(e) => updateAdditionalItem(item.id, 'description', e.target.value)}
                                                    placeholder="e.g. Wall 3 Insulated Plaster Board"
                                                    data-testid={`item-description-${idx}`}
                                                    className={inputCls}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Additional Hours</label>
                                                    <input
                                                        type="number" inputMode="decimal" step="0.5" min="0"
                                                        value={item.hours}
                                                        onChange={(e) => updateAdditionalItem(item.id, 'hours', e.target.value)}
                                                        placeholder="0"
                                                        data-testid={`item-hours-${idx}`}
                                                        className={inputCls}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Additional Value</label>
                                                    <div className="relative">
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">&pound;</span>
                                                        <input
                                                            type="number" inputMode="decimal" step="0.01" min="0"
                                                            value={item.value}
                                                            onChange={(e) => updateAdditionalItem(item.id, 'value', e.target.value)}
                                                            placeholder="0.00"
                                                            data-testid={`item-value-${idx}`}
                                                            className={`${inputCls} pl-7`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ==================== PHOTOS ==================== */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="photos-section">
                            <SectionHeading>Photos</SectionHeading>
                            <input
                                ref={photoInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                onChange={handlePhotoUpload}
                                className="hidden"
                                data-testid="photo-file-input"
                            />
                            <div className="flex flex-wrap gap-2">
                                {photos.map((photo) => (
                                    <div key={photo.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                                        <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                                        <button
                                            type="button"
                                            onClick={() => removePhoto(photo.id)}
                                            data-testid={`remove-photo-${photo.id}`}
                                            className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => photoInputRef.current?.click()}
                                    data-testid="upload-photo-btn"
                                    className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <Camera className="w-5 h-5" />
                                    <span className="text-[10px] font-medium">Add</span>
                                </button>
                            </div>
                        </div>

                        {/* ==================== NOTES ==================== */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="notes-section">
                            <SectionHeading>Notes</SectionHeading>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Any additional comments..."
                                rows={3}
                                data-testid="notes-input"
                                className="w-full px-3 py-2.5 text-sm font-medium text-slate-900 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors placeholder:text-slate-300 resize-none"
                            />
                        </div>

                        {/* ==================== SAVE BUTTON ==================== */}
                        <button
                            type="button"
                            onClick={handleSaveRoom}
                            data-testid="save-room-btn"
                            className="w-full h-13 py-3.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-heading text-base font-bold uppercase tracking-wider rounded-xl transition-colors"
                        >
                            Save Room
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
